// Pure pieces of the coordinate-system preference, kept free of mapbox-gl/proj4 so they are
// unit-testable. GridRefPreference.ts wires them to window.user / localStorage / GraphQL.
//
// Tokens are the server wire values shared with iOS and Android: GbGrid | IeGrid | NzGrid | LatLon | LatLonDms.

export const GRID_REF_STORAGE_KEY = 'hiiker_grid_ref';

export const GRID_REF_TOKENS = ['GbGrid', 'IeGrid', 'NzGrid', 'LatLon', 'LatLonDms'] as const;
export type GridRefToken = typeof GRID_REF_TOKENS[number];

export function isGridRefToken(v: unknown): v is GridRefToken {
  return typeof v === 'string' && (GRID_REF_TOKENS as ReadonlyArray<string>).includes(v);
}

/**
 * Precedence: the signed-in user's server value > this device's saved choice > the page's
 * server-injected default (window.gridRef, used on logged-out/embed views) > LatLon.
 */
export function resolveGridRefToken(
  userToken: unknown,
  storedToken: unknown,
  pageToken: unknown,
): GridRefToken {
  if (isGridRefToken(userToken)) return userToken;
  if (isGridRefToken(storedToken)) return storedToken;
  if (isGridRefToken(pageToken)) return pageToken;
  return 'LatLon';
}

export interface GridRefPersistPlan {
  /** Always: keeps the choice across reloads and is the only store when signed out. */
  writeLocalStorage: true;
  /** Only when a user object exists on the page, so preference() sees the change immediately. */
  updateWindowUser: boolean;
  /** Only when signed in: the apps read User.gridReference, so this is the cross-platform sync. */
  pushToServer: boolean;
}

export function gridRefPersistPlan(signedIn: boolean): GridRefPersistPlan {
  return { writeLocalStorage: true, updateWindowUser: signedIn, pushToServer: signedIn };
}

export function readStoredGridRefToken(storage: Pick<Storage, 'getItem'> | null | undefined): GridRefToken | null {
  try {
    const v = storage?.getItem(GRID_REF_STORAGE_KEY);
    return isGridRefToken(v) ? v : null;
  } catch {
    return null;
  }
}

export function writeStoredGridRefToken(storage: Pick<Storage, 'setItem'> | null | undefined, token: GridRefToken): void {
  try {
    storage?.setItem(GRID_REF_STORAGE_KEY, token);
  } catch {
    /* private mode / quota */
  }
}
